import React from "react";
import { cx } from "utils/join-classnames";
import { SocialPlatform } from "core/models";
import { SocialSettingCard, AddSocialSettingModal } from "ui/components";
import * as localStorageKeys from "core/constants/localStorageKeys";
import {
    configpublishsetting,
    createSocialSetting,
    deletePublishsetting,
    updatePublishsetting,
} from "core/services";
import Configuration from "Configuration";
import { useNotifications } from "ui/context/NotificationsContext";
import { useSessionContext } from "ui/context/SessionContext";
import { usePublishContext } from "ui/context/PublishContext";
import usePermissions from "ui/hooks/usePermissions";
import styles from "./Social.module.scss";

const SettingsPage: React.FC = () => {
    const { addNotification } = useNotifications();
    const sessionContext = useSessionContext();
    const publishContext = usePublishContext();
    const userPermissions = usePermissions();
    const [addModalPlatform, setAddModalPlatform] =
        React.useState<SocialPlatform>();

    const openInNewTab = (url: string) => {
        const newWindow = window.open(url, "_self", "noopener,noreferrer");
        if (newWindow) {
            newWindow.opener = null;
        }
    };
    const getConnectUrl = async (
        publisherKey: string,
        platform: SocialPlatform,
    ): Promise<string | undefined> => {
        const OAUTH_URL = Configuration.get("oauthURL");
        let operation = "startauthentication";
        if (platform === "facebook" || platform === "youtube") {
            operation = "getauthredirecturl";
        }
        const requests = {
            youtube: { redirecturl: `${OAUTH_URL}youtube` },
            facebook: { redirecturl: `${OAUTH_URL}facebook` },
            twitter: { redirecturl: `${OAUTH_URL}twitter` },
        };

        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected project");
            }
            const result = await configpublishsetting(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                publisherKey,
                operation,
                requests[platform],
            );
            if (result.error || !result.value) {
                throw new Error(result.error);
            }
            let redirectURL;
            if (platform === "facebook") {
                localStorage.setItem(localStorageKeys.FB_ACC_KEY, publisherKey);
                localStorage.setItem(
                    localStorageKeys.FB_REDIRECT_URL,
                    requests[platform].redirecturl,
                );
                redirectURL = result.value;
            } else if (platform === "youtube") {
                redirectURL = `${result.value}`;
                localStorage.setItem(localStorageKeys.YT_ACC_KEY, publisherKey);
                localStorage.setItem(
                    localStorageKeys.YT_REDIRECT_URL,
                    requests[platform].redirecturl,
                );
            } else {
                localStorage.setItem(localStorageKeys.TW_ACC_KEY, publisherKey);
                localStorage.setItem(
                    localStorageKeys.TW_VERIFY_TOKEN,
                    JSON.parse(result.value).token,
                );
                localStorage.setItem(
                    localStorageKeys.TW_VERIFY_SECRET,
                    JSON.parse(result.value).tokensecret,
                );
                redirectURL = JSON.parse(result.value).redirecturl;
            }

            return redirectURL;
        } catch (err) {
            addNotification(
                "error",
                "Error getting connect URL",
                (err as Error).message,
                5000,
            );
            return undefined;
        }
    };

    const handleConnectNew = (platform: SocialPlatform): void => {
        setAddModalPlatform(platform);
    };

    const createNewSocialSetting = async (
        platform: SocialPlatform,
        socialSettingName: string,
    ): Promise<void> => {
        const randomInt = Math.ceil(Math.random() * 100000);
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected projects");
            }
            const socialSettingKey = `${encodeURIComponent(
                sessionContext.selectedProject.name,
            )}_${encodeURIComponent(platform)}_${randomInt}`;

            const result = await createSocialSetting(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                `default_${platform}`,
                socialSettingName,
                socialSettingKey,
            );
            if (result.error || !result.value) {
                throw new Error();
            }
            const connectUrl = await getConnectUrl(result.value.key, platform);
            if (!connectUrl) {
                throw Error("no connectUrl");
            }
            openInNewTab(connectUrl);
        } catch (err) {
            addNotification(
                "error",
                "Error creating new social connection",
                (err as Error).message,
                5000,
            );
        }
    };

    const handleCloseAddModal = (): void => {
        setAddModalPlatform(undefined);
    };
    const handleSaveSocialSetting = (socialSettingName: string): void => {
        if (!addModalPlatform) {
            return;
        }
        createNewSocialSetting(addModalPlatform, socialSettingName);
    };

    const handleReconnect = async (
        accountKey: string,
        platform: SocialPlatform,
    ): Promise<void> => {
        try {
            const connectUrl = await getConnectUrl(accountKey, platform);
            if (!connectUrl) {
                throw Error("no connectUrl");
            }
            openInNewTab(connectUrl);
        } catch (err) {
            addNotification(
                "error",
                "Error reconnecting",
                "Please try again or contact us",
                5000,
            );
        }
    };
    const handleDelete = async (accountKey: string): Promise<void> => {
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected project");
            }
            const result = await deletePublishsetting(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                accountKey,
            );
            if (result.error) {
                throw new Error(result.error);
            }
            publishContext.reloadSocialAccounts();
        } catch (err) {
            addNotification(
                "error",
                "Can not delete",
                (err as Error).message,
                5000,
            );
        }
    };
    const handleChangeAccountName = async (
        accountKey: string,
        newAccountName: string,
    ): Promise<void> => 
        // eslint-disable-next-line no-async-promise-executor
         new Promise(async (resolve, reject) => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                if (!sessionContext.selectedProject) {
                    throw Error("no selected project");
                }
                const result = await updatePublishsetting(
                    sessionContext.session.token,
                    sessionContext.selectedProject.id,
                    accountKey,
                    newAccountName,
                );
                if (result.error || !result.value) {
                    throw Error("Error while saving edited account names");
                }
                resolve();
            } catch (err) {
                addNotification(
                    "error",
                    "Something went wrong",
                    (err as Error).message,
                    5000,
                );
                reject();
            }
        })
    ;

    const facebookAccounts =
        publishContext.accounts.filter(
            (option) => option.publisherkey === "default_facebook",
        ) || [];

    const twitterAccounts =
        publishContext.accounts.filter(
            (option) => option.publisherkey === "default_twitter",
        ) || [];

    const youtubeAccounts =
        publishContext.accounts.filter(
            (option) => option.publisherkey === "default_youtube",
        ) || [];

    if (!userPermissions.hasFeatureConfigureShare) {
        return (
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Not available
                    </h1>
                    <span className={styles.heading__desc}>
                        Upgrade your plan to manage social media accounts
                    </span>
                </div>
            </div>
        );
    }
    if (!userPermissions.hasRightToConfigureShare) {
        return (
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Not available
                    </h1>
                    <span className={styles.heading__desc}>
                        You don&#39;t seem to have permission, please contact
                        your project&#39;s admin to configure social media
                        accounts
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Social media accounts for{" "}
                        {sessionContext.selectedProject?.name}
                    </h1>
                    <span className={styles.heading__desc}>
                        Connect social media accounts to your project to share
                        clips directly to your channels.
                    </span>
                </div>
                <div className={styles.heading__right} />
            </div>

            <SocialSettingCard
                platform="facebook"
                accounts={facebookAccounts}
                onConnectNew={handleConnectNew}
                onDelete={handleDelete}
                onChangeAccountName={handleChangeAccountName}
                onReconnect={handleReconnect}
            />
            <SocialSettingCard
                platform="twitter"
                accounts={twitterAccounts}
                onConnectNew={handleConnectNew}
                onDelete={handleDelete}
                onChangeAccountName={handleChangeAccountName}
                onReconnect={handleReconnect}
            />
            <SocialSettingCard
                platform="youtube"
                accounts={youtubeAccounts}
                onConnectNew={handleConnectNew}
                onDelete={handleDelete}
                onChangeAccountName={handleChangeAccountName}
                onReconnect={handleReconnect}
            />
            {addModalPlatform && (
                <AddSocialSettingModal
                    platform={addModalPlatform}
                    onSave={handleSaveSocialSetting}
                    onClose={handleCloseAddModal}
                />
            )}
        </div>
    );
};

export default SettingsPage;
